import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/CommonExport';
import { TextField, Grid, Autocomplete, CircularProgress, Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Papa from 'papaparse';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';

const getTodayDate = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    const local = new Date(now.getTime() - offsetMs);
    return local.toISOString().split('T')[0];
};

const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const offsetMs = tomorrow.getTimezoneOffset() * 60000;
    const local = new Date(tomorrow.getTime() - offsetMs);
    return local.toISOString().split('T')[0];
};

function coerceSummary(summary) {
    if (!summary) return null;
    const n = (v) => Number(v) || 0;
    return {
        totalPosts: n(summary.total_posts),
        taggedPosts: n(summary.tagged_posts),
        untaggedPosts: n(summary.untagged_posts),
        totalViews: n(summary.total_views),
        totalLikes: n(summary.total_likes),
        totalComments: n(summary.total_comments),
        totalShares: n(summary.total_shares),
    };
}

function buildTagsRows(data) {
    const tags = Array.isArray(data?.tags) ? data.tags : [];
    const rows = tags.map((tag) => ({
        ...tag,
        id: tag.id ?? `tag-${tag.tag}`,
    }));

    if (data?.untagged) {
        rows.push({
            ...data.untagged,
            id: data.untagged.id ?? 'untagged',
            tag: data.untagged.tag || 'Untagged',
            is_untagged: true,
        });
    }

    return rows;
}

function TagsAnalytics() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(() => getTodayDate());
    const [endDate, setEndDate] = useState(() => getTomorrowDate());
    const [matchFilter, setMatchFilter] = useState(null);
    const [cidFilter, setCidFilter] = useState(null);
    const [mediaTypeFilter, setMediaTypeFilter] = useState('');
    const [creatorFilter, setCreatorFilter] = useState(null);
    const [matchData, setMatchData] = useState([]);
    const [competitionData, setCompetitionData] = useState([]);
    const [creatorsData, setCreatorsData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    
    // Add Tag Modal States
    const [openAddTagModal, setOpenAddTagModal] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [addingTag, setAddingTag] = useState(false);

    // Media type options
    const mediaTypes = [
        { label: 'All', value: null },
        { label: 'Image', value: 'image' },
        { label: 'Video', value: 'video/mp4' }
    ];

    useEffect(() => {
        // Fetch competitions data
        axios.get(`${BASE_URL}/admin/competitions/get_competitions?start_date=${startDate}&end_date=${endDate}`)
            .then((result) => {
                console.log(result.data.data.competitions)
                setCompetitionData(result.data.data.competitions);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [startDate, endDate]);

    useEffect(() => {
        // Fetch creators data
        axios.get(`${BASE_URL}/admin/users/creators`)
            .then((result) => {
                console.log(result.data.data.creators)
                setCreatorsData(result.data.data.creators);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        const fetchMatches = async () => {
            const requestBody = {};
            if (startDate) requestBody.startDate = startDate;
            if (endDate) requestBody.endDate = endDate;
            if (cidFilter) requestBody.cid = cidFilter;
            console.log(requestBody)

            try {
                const response = await axios.post(`${BASE_URL}/tracking/matches`, requestBody);
                console.log(response.data)
                setMatchData(response.data.data.users);
            } catch (error) {
                console.error('Error fetching matches:', error);
            }
        };

        fetchMatches();
    }, [startDate, endDate, cidFilter]);

    const applyTagsResponse = (data) => {
        const rows = buildTagsRows(data);
        setFilteredData(rows);
        setSummary(coerceSummary(data?.summary));
    };

    const fetchTagsData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (matchFilter) params.append('match_id', matchFilter.match_id);
            if (cidFilter) params.append('cid', cidFilter);
            if (mediaTypeFilter) params.append('media_type', mediaTypeFilter);
            if (creatorFilter) params.append('user_id', creatorFilter.user_id);
            const response = await axios.get(`${BASE_URL}/admin/feeds/tags/analytics?${params.toString()}`);
            applyTagsResponse(response.data.data);
        } catch (error) {
            console.error('Error fetching tags data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTagsData();
    }, [startDate, endDate, matchFilter, cidFilter, mediaTypeFilter, creatorFilter]);

    const totals = summary || {
        totalPosts: 0,
        taggedPosts: 0,
        untaggedPosts: 0,
        totalViews: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
    };

    const exportToCSV = () => {
        const csvData = filteredData.map(item => ({
            Tag: item.tag,
            Posts: item.post_count,
            Views: item.view_count,
            Likes: item.like_count,
            Comments: item.comment_count,
            Shares: item.share_count,
        }));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tags_analytics.csv';
        a.click();
    };
    
    const handleAddTag = async () => {
        if (!newTagName.trim()) return;
        
        setAddingTag(true);
        try {
            const response = await axios.post(`${BASE_URL}/admin/tags/add-tags`, {
                tag: newTagName.trim()
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json, text/plain, */*'
                }
            });
            console.log('Tag added successfully:', response.data);
            
            setOpenAddTagModal(false);
            setNewTagName('');
            await fetchTagsData();
        } catch (error) {
            console.error('Error adding tag:', error);
        } finally {
            setAddingTag(false);
        }
    };

    const handleOpenAddTagModal = () => {
        setOpenAddTagModal(true);
        setNewTagName('');
    };

    const handleCloseAddTagModal = () => {
        setOpenAddTagModal(false);
        setNewTagName('');
    };

    const columns = [
        { field: 'tag', headerName: 'Tag', flex: 1 },
        { field: 'post_count', headerName: 'Posts', flex: 1, type: 'number' },
        { field: 'view_count', headerName: 'Views', flex: 1, type: 'number' },
        { field: 'like_count', headerName: 'Likes', flex: 1, type: 'number' },
        { field: 'comment_count', headerName: 'Comments', flex: 1, type: 'number' },
        { field: 'share_count', headerName: 'Shares', flex: 1, type: 'number' },
    ];

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <div className="page-title">
                        <div className="row">
                            <div className="col-6 d-flex align-items-center">
                                <IconButton 
                                    onClick={() => navigate(-1)}
                                    sx={{ mr: 2 }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <h3>Tags Analytics</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="From Date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="To Date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={competitionData}
                                                getOptionLabel={(option) => option.title || ''}
                                                isOptionEqualToValue={(option, value) => option.cid === value.cid}
                                                value={competitionData.find(comp => comp.cid === cidFilter) || null}
                                                onChange={(event, newValue) => {
                                                    setCidFilter(newValue ? newValue.cid : null);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Competition" />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={matchData}
                                                getOptionLabel={(option) => option.title + ' - ' + option.subtitle + ' - ' + option.match_id || ''}
                                                isOptionEqualToValue={(option, value) => option.match_id === value.match_id}
                                                value={matchFilter}
                                                onChange={(event, newValue) => {
                                                    setMatchFilter(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Match" />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={mediaTypes}
                                                getOptionLabel={(option) => option.label}
                                                value={mediaTypes.find(type => type.value === mediaTypeFilter) || mediaTypes[0]}
                                                onChange={(event, newValue) => {
                                                    setMediaTypeFilter(newValue ? newValue.value : '');
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Media Type" />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={creatorsData}
                                                getOptionLabel={(option) => option.username || ''}
                                                isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                                                value={creatorFilter}
                                                onChange={(event, newValue) => {
                                                    setCreatorFilter(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Creator" />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                    
                                    <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                                        <Button variant="contained" color="primary" onClick={exportToCSV}>Export to CSV</Button>
                                        <Button variant="contained" color="secondary" onClick={handleOpenAddTagModal}>Add Tag</Button>
                                    </Box>

                                    {/* Add Tag Modal */}
                                    <Dialog open={openAddTagModal} onClose={handleCloseAddTagModal} maxWidth="sm" fullWidth>
                                        <DialogTitle>Add New Tag</DialogTitle>
                                        <DialogContent>
                                            <TextField
                                                autoFocus
                                                margin="dense"
                                                label="Tag Name"
                                                type="text"
                                                fullWidth
                                                variant="outlined"
                                                value={newTagName}
                                                onChange={(e) => setNewTagName(e.target.value)}
                                                placeholder="Enter tag name"
                                                disabled={addingTag}
                                            />
                                        </DialogContent>
                                        <DialogActions>
                                            <MuiButton onClick={handleCloseAddTagModal} disabled={addingTag}>
                                                Cancel
                                            </MuiButton>
                                            <MuiButton 
                                                onClick={handleAddTag} 
                                                variant="contained" 
                                                color="primary"
                                                disabled={!newTagName.trim() || addingTag}
                                            >
                                                {addingTag ? 'Adding...' : 'Add Tag'}
                                            </MuiButton>
                                        </DialogActions>
                                    </Dialog>

                                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#e6f3ff', borderRadius: 1 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Summary
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Posts</Typography>
                                                <Typography variant="h6">{totals.totalPosts}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Tagged Posts</Typography>
                                                <Typography variant="h6">{totals.taggedPosts}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Untagged Posts</Typography>
                                                <Typography variant="h6">{totals.untaggedPosts}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Views</Typography>
                                                <Typography variant="h6">{totals.totalViews}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Likes</Typography>
                                                <Typography variant="h6">{totals.totalLikes}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Comments</Typography>
                                                <Typography variant="h6">{totals.totalComments}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Shares</Typography>
                                                <Typography variant="h6">{totals.totalShares}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {loading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <div style={{ height: 600, width: '100%' }}>
                                            <DataGrid
                                                rows={filteredData}
                                                columns={columns}
                                                pageSize={10}
                                                rowsPerPageOptions={[10, 25, 50]}
                                                checkboxSelection={false}
                                                disableSelectionOnClick
                                                getRowId={(row) => row.id}
                                                getRowClassName={(params) =>
                                                    params.row.is_untagged ? 'untagged-row' : ''
                                                }
                                                sx={{
                                                    '& .untagged-row': {
                                                        backgroundColor: '#fff8e1',
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Component>
    );
}

export default TagsAnalytics; 